import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Flag,
  Bike,
  Presentation,
  GraduationCap,
  Newspaper,
  Megaphone,
  PlayCircle,
  BookOpen,
  Briefcase,
  MoreHorizontal,
  FileText,
  FlaskConical,
  Trophy,
  Mic,
  Target,
  ListChecks,
  Compass,
  Calendar,
  Users,
  Clapperboard,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { MobileMenu } from './MobileMenu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { useApp } from '../contexts/AppContext';
import useAudience, { AUDIENCES } from '../hooks/useAudience';
import { useTeacherMode } from '../hooks/useTeacherMode';

/**
 * BottomNav v5 — Refonte juin 2026 « Outil pro mobile-first »
 *
 * 5 onglets pour chaque rôle, dans une même logique factuelle et concrète :
 *
 *   ÉLÈVE / VISITEUR
 *     Accueil · Plateau · Circulation · Examen · Magazine
 *     → pour s'inspirer du concret de la conduite réelle dès la home
 *
 *   ENSEIGNANT / ADMIN
 *     Plateau · Circulation · Salle · Examen · Sensibilisation
 *     → vrai outil de travail pro pour donner cours / réviser / monter des actions
 *
 * L'enseignant ne voit JAMAIS les items élèves. Sa nav est complètement isolée.
 */
const STUDENT_ITEMS = [
  { to: '/magazine', label: 'MAG', icon: Newspaper, key: 'magazine', color: '#ea580c' },
  { to: '/shorts', label: 'SHORTS', icon: Clapperboard, key: 'shorts', color: '#cf2031', center: true },
  { to: '/epreuves/plateau', label: 'PLATEAU', icon: Flag, key: 'plateau', color: '#7c3aed' },
  { to: '/epreuves/circulation', label: 'ROUTE', icon: Bike, key: 'circulation', color: '#059669' },
  { to: '/moto-ecoles', label: 'ANNUAIRE', icon: Compass, key: 'annuaire', color: '#1d4ed8' },
];

const TEACHER_ITEMS = [
  { to: '/moniteur/planning', label: 'PLANNING', icon: Calendar, key: 'planning', color: '#f43f5e' },
  { to: '/bibliotheque#permis-am', label: 'SALLE', icon: BookOpen, key: 'salle', color: '#1d4ed8' },
  { to: '/bibliotheque#plateau-a2', label: 'PLATEAU', icon: Flag, key: 'plateau', color: '#cf2031' },
  { to: '/bibliotheque#circulation-a2', label: 'ROUTE', icon: Bike, key: 'circulation', color: '#7c3aed' },
  { to: '/moniteur/eleves', label: 'ÉLÈVES', icon: Users, key: 'eleves', color: '#16a34a' },
];

/* Sheet CCS — MASQUÉ Phase 30 (recentrage auto-école) */
const CCS_SHEET_SECTIONS = [
  {
    titre: 'Contenu pédagogique',
    accent: '#0891b2',
    items: [
      { to: '/enseignant/livret-apprentissage', label: 'Livret REMC', icon: FileText, sub: 'Référentiel pédagogique' },
      { to: '/enseignant/fondements-pedagogie', label: 'Fondements pédagogie', icon: GraduationCap, sub: 'Bases théoriques' },
    ],
  },
];

/* Futur enseignant — MASQUÉ Phase 30 (recentrage auto-école) */
const FUTUR_ENS_ITEMS = [];

export const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useApp();
  const { audience } = useAudience();
  const { teacherMode } = useTeacherMode();
  const [moreOpen, setMoreOpen] = useState(false);
  const [ccsOpen, setCcsOpen] = useState(false);

  // La nav suit d'abord la SECTION visitée (BO élève / BO enseignant / admin),
  // puis à défaut le rôle utilisateur. Ainsi un admin qui inspecte le BO élève
  // voit bien la nav élève (Plateau, Circu, Tutos, Mag).
  const pathname = location.pathname || '';
  const isEleveSection = pathname.startsWith('/eleve/');
  const isTeacherSection = pathname.startsWith('/moniteur/') || pathname.startsWith('/admin/') || pathname.startsWith('/secretariat/') || pathname.startsWith('/enseignant');

  const roleIsTeacherOrAdmin = !!(user && (user.role === 'enseignant' || user.role === 'admin'));
  // Priorité section > mode teacher (jeton ?teacher=1) > rôle
  const isTeacherOrAdmin = isTeacherSection || (!isEleveSection && (teacherMode || roleIsTeacherOrAdmin));
  const isFuturEnseignant = !isTeacherOrAdmin && audience === AUDIENCES.FUTUR_ENSEIGNANT;
  const items = isTeacherOrAdmin
    ? TEACHER_ITEMS
    : (isFuturEnseignant ? FUTUR_ENS_ITEMS : STUDENT_ITEMS);

  const isActive = (to) => {
    if (!to) return false;
    // Ignore le hash pour la comparaison de route (ex. /bibliotheque#plateau-a2)
    const cleanTo = to.split('#')[0];
    if (cleanTo === '/') return location.pathname === '/';
    if (cleanTo === '/enseignant') return location.pathname === '/enseignant';
    return location.pathname.startsWith(cleanTo);
  };

  const handleClick = (item) => {
    if (item.key === 'more') {
      setMoreOpen(true);
      return;
    }
    if (item.key === 'ccs') {
      setCcsOpen(true);
      return;
    }
    navigate(item.to);
  };

  return (
    <>
      {/* Mini-bandeau « ESPACE PRO » au-dessus de la nav enseignant */}
      {isTeacherOrAdmin && (
        <div
          className="no-print fixed left-0 right-0 z-40 pointer-events-none lg:hidden"
          style={{ bottom: `calc(64px + env(safe-area-inset-bottom))` }}
          aria-hidden="true"
        >
          <div className="max-w-6xl mx-auto flex justify-center">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-t-md text-[9px] font-black uppercase tracking-[0.18em] text-white shadow-sm"
              style={{ background: 'linear-gradient(135deg, #1f2937, #374151)' }}
              data-testid="teacher-mode-badge"
            >
              <GraduationCap className="w-2.5 h-2.5" /> Espace Pro
            </span>
          </div>
        </div>
      )}

      <nav
        className="no-print fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border lg:hidden"
        data-testid="mobile-bottom-nav"
        data-mode={isTeacherOrAdmin ? 'teacher' : 'student'}
        aria-label={isTeacherOrAdmin ? 'Navigation Espace Pro ECSR' : 'Navigation principale'}
        style={{
          boxShadow: '0 -8px 24px -12px rgba(36, 28, 14, 0.18)',
        }}
      >
        <div
          className="max-w-6xl mx-auto grid grid-cols-5"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {items.map(({ to, label, icon: Icon, key, color, center }) => {
            const active = isActive(to) || (key === 'more' && moreOpen);
            const itemColor = color || 'hsl(var(--primary))';
            if (center) {
              // Onglet Shorts central mis en relief (effet "clap" rouge).
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleClick({ to, key })}
                  data-testid={`bottom-nav-${key}`}
                  aria-label={label}
                  aria-current={active ? 'page' : undefined}
                  className="relative flex flex-col items-center justify-center gap-1 py-2 px-1 active:scale-95 transition-all"
                >
                  <span
                    className="relative flex items-center justify-center w-12 h-12 rounded-full shadow-lg -mt-5"
                    style={{
                      background: `linear-gradient(135deg, ${itemColor} 0%, #7c0d1a 100%)`,
                      boxShadow: `0 10px 30px -8px ${itemColor}aa, 0 4px 12px -4px rgba(0,0,0,0.3)`,
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" strokeWidth={2.2} aria-hidden="true" />
                    {/* Halo pulsant pour signaler le hotspot "NEW" (prefers-reduced-motion respecté
                        via l'absence de keyframes complexes — simple scale + opacity). */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 rounded-full animate-ping"
                      style={{
                        background: `${itemColor}`,
                        opacity: 0.35,
                        animationDuration: '2.4s',
                      }}
                    />
                    {/* Badge NEW rouge/blanc en haut-droite */}
                    <span
                      aria-hidden="true"
                      className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-[14px] px-1 rounded-full bg-white text-[8px] font-black uppercase tracking-wider shadow-md border border-red-200"
                      style={{ color: '#cf2031' }}
                    >
                      NEW
                    </span>
                  </span>
                  <span
                    className="text-[9px] sm:text-[10px] font-black tracking-[0.08em] leading-none mt-0.5 uppercase"
                    style={{ color: active ? itemColor : 'hsl(var(--muted-foreground))' }}
                  >
                    {label}
                  </span>
                </button>
              );
            }
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleClick({ to, key })}
                data-testid={`bottom-nav-${key}`}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'relative flex flex-col items-center justify-center gap-1 py-3 px-1 min-w-0 active:scale-95 transition-all duration-200',
                  active ? '' : 'text-muted-foreground hover:text-foreground',
                )}
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  color: active ? itemColor : undefined,
                }}
              >
                {active && (
                  <span
                    className="absolute inset-x-2 inset-y-1 rounded-2xl pointer-events-none"
                    style={{
                      background: `radial-gradient(ellipse at center, ${itemColor}26 0%, transparent 70%)`,
                    }}
                    aria-hidden="true"
                  />
                )}
                <Icon
                  className="w-6 h-6 relative flex-shrink-0"
                  strokeWidth={active ? 2.4 : 1.8}
                  aria-hidden="true"
                />
                <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.04em] leading-none mt-1 relative w-full text-center truncate">
                  {label}
                </span>
                {active && (
                  <span
                    className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-12 rounded-full"
                    style={{
                      background: itemColor,
                      boxShadow: `0 0 8px ${itemColor}aa`,
                    }}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Sheet "Plus" — réutilise le MobileMenu (qui s'adapte aussi au rôle). */}
      <MobileMenu open={moreOpen} onOpenChange={setMoreOpen} />

      {/* Sheet "CCS" — toutes les ressources CCS 2 Roues bien rangées */}
      <Sheet open={ccsOpen} onOpenChange={setCcsOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-safe max-h-[88vh] overflow-y-auto" data-testid="ccs-sheet">
          <SheetHeader className="text-left mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-white" style={{ background: '#ea580c' }}>
                <GraduationCap className="w-5 h-5" />
              </span>
              <div>
                <SheetTitle className="text-xl font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  CCS 2 Roues
                </SheetTitle>
                <SheetDescription className="text-xs">
                  Préparation · Déroulé · Contenu pédagogique
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {CCS_SHEET_SECTIONS.map((section) => (
            <section key={section.titre} className="mb-5">
              <h3
                className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] mb-2"
                style={{ color: section.accent }}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: section.accent }} />
                {section.titre}
              </h3>
              <ul className="space-y-1.5">
                {section.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <li key={it.to}>
                      <button
                        type="button"
                        onClick={() => { setCcsOpen(false); navigate(it.to); }}
                        className="w-full text-left rounded-xl border-2 border-border bg-card hover:border-current transition-colors p-3 flex items-start gap-3"
                        style={{ borderColor: `${section.accent}33` }}
                        data-testid={`ccs-sheet-${it.to.replace(/\//g, '-')}`}
                      >
                        <span
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
                          style={{ background: section.accent }}
                        >
                          <Icon className="w-4 h-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-black leading-tight">{it.label}</div>
                          {it.sub && (
                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{it.sub}</div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          <button
            type="button"
            onClick={() => { setCcsOpen(false); navigate('/preparation-ccs'); }}
            className="w-full mt-2 rounded-xl py-3 text-white font-black uppercase tracking-wider text-sm"
            style={{ background: '#ea580c' }}
            data-testid="ccs-sheet-hub"
          >
            <Compass className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Hub Préparation CCS · Parcours 6 étapes
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
};
